import { useCallback, useEffect, useRef, useState } from 'react';
import { useQueryClient } from 'react-query';
import {
	deleteFileFromSharePoint,
	uploadFileToSharePoint,
} from '@/services/common';
import { fileToBase64 } from '@/utils';

// Comprehensive list of accepted file extensions
export const ACCEPTED_FILE_EXTENSIONS = [
	// PDFs
	'.pdf',
	// Word Documents
	'.doc',
	'.docx',
	// Images
	'.jpg',
	'.jpeg',
	'.png',
	'.gif',
	'.bmp',
	'.tiff',
	'.webp',
	// Excel/Spreadsheets
	'.xls',
	'.xlsx',
	'.xlsm',
	'.csv',
	// Apple iWork
	'.pages',
	'.numbers',
	// Text files
	'.txt',
	'.rtf',
	// RFX formats
	'.rfp',
	'.rfq',
	'.rfi',
	// Compressed files
	'.zip',
] as const;

export interface UploadedFile {
	id: string;
	name: string; // Original file name for display
	size: number;
	type: string;
	file: File | any;
	sharePointUrl: string;
	sharePointFileName: string; // Actual filename stored in SharePoint
	clinic: string;
	folderName: string;
}

export interface UseFileUploadProps {
	files: UploadedFile[];
	onFilesChange: (files: UploadedFile[]) => void;
	clinic: string;
	clinicName: string;
	folderName: string;
	onError?: (error: string) => void;
	disabled?: boolean; // Flag to indicate if upload should be disabled
}

export const useFileUpload = ({
	files,
	onFilesChange,
	clinic,
	clinicName,
	folderName,
	onError,
	disabled = false,
}: UseFileUploadProps) => {
	const queryClient = useQueryClient();
	const [uploadError, setUploadError] = useState<string>('');
	const [isUploading, setIsUploading] = useState(false);
	const [isDeleting, setIsDeleting] = useState(false);
	const filesRef = useRef<UploadedFile[]>(files);
	const failedFilesRef = useRef<string[]>([]);

	// Keep ref in sync with files prop
	useEffect(() => {
		filesRef.current = files;
	}, [files]);

	// Validate file extension
	const isValidFileExtension = useCallback((fileName: string): boolean => {
		const lowerFileName = fileName.toLowerCase();
		return ACCEPTED_FILE_EXTENSIONS.some(ext => lowerFileName.endsWith(ext));
	}, []);

	// Parse and clean error messages to make them user-friendly
	const parseErrorMessage = useCallback((error: any, defaultMessage: string): string => {
		let message = defaultMessage;

		if (error instanceof Error) {
			message = error.message;
		} else if (typeof error === 'string') {
			message = error;
		} else if (error?.message) {
			message = error.message;
		}

		// Remove technical error codes and make message user-friendly
		// Remove patterns like "Error 500:", "ERR_", "[ERROR]", etc.
		message = message
			.replace(/error\s*\d+:/gi, '') // Remove "Error 500:" patterns
			.replace(/\[error\]/gi, '') // Remove "[ERROR]" tags
			.replace(/err_\w+/gi, '') // Remove "ERR_NETWORK" patterns
			.replace(/code:\s*\d+/gi, '') // Remove "code: 500" patterns
			.replace(/status:\s*\d+/gi, '') // Remove "status: 500" patterns
			.trim();

		// If message is too technical or empty after cleaning, use default
		if (!message || message.length < 5 || /^[A-Z_]+$/.test(message)) {
			message = defaultMessage;
		}

		return message;
	}, []);

	const uploadToSharePoint = useCallback(
		async (file: File): Promise<UploadedFile | null> => {
			try {
				// Validate file extension first
				if (!isValidFileExtension(file.name)) {
					throw new Error(
						'This file type is not supported. Please upload a valid file format.'
					);
				}

				// Sanitize filename: remove spaces and special characters
				const sanitizedName = file.name
					.replace(/\s+/g, '_') // Replace spaces with underscores
					.replace(/[^a-zA-Z0-9._-]/g, '') // Remove special characters except . _ -
					.replace(/_+/g, '_'); // Replace multiple underscores with single
				
				
				// Convert file to base64
				const base64Data = await fileToBase64(file);

				// Upload to SharePoint with sanitized filename
				const response = await uploadFileToSharePoint({
					fileBase64: base64Data,
					fileName: sanitizedName, // Use sanitized name
					clinic: `${clinicName.replaceAll('&', 'and')}_${clinic.toUpperCase()}`,
					folderName,
				});

				if (response.status === 200 && response.data) {
					// Return successfully uploaded file
					return {
						id: `${Date.now()}-${Math.random()}`,
						name: file.name, // Keep original name for display
						size: file.size,
						type: file.type,
						file,
						sharePointUrl: response.data.url,
						sharePointFileName: response.data.fileName, // Use SharePoint's returned filename
						clinic,
						folderName,
					};
				} else {
					// Use user-friendly error message
					const cleanMessage = parseErrorMessage(
						response.message,
						'Unable to upload file. Please try again.'
					);
					throw new Error(cleanMessage);
				}
			} catch (error) {
				// Parse error to make it user-friendly
				const errorMessage = parseErrorMessage(
					error,
					'Unable to upload file. Please check your connection and try again.'
				);

				// Track failed file
				failedFilesRef.current.push(`${file.name}: ${errorMessage}`);
				if (onError) {
					onError(errorMessage);
				}

				return null;
			}
		},
		[clinic, folderName, onError, isValidFileExtension, parseErrorMessage],
	);

	const handleFileUpload = useCallback(
		async (fileList: FileList) => {
			// Validate that clinic is provided before uploading
			if (disabled || !clinic) {
				const errorMsg = 'Please select a clinic before uploading files';
				setUploadError(errorMsg);
				if (onError) {
					onError(errorMsg);
				}
				return;
			}

			setUploadError(''); // Clear previous errors
			failedFilesRef.current = []; // Clear failed files list
			setIsUploading(true); // Show loader

			const fileArray = Array.from(fileList);

			try {
				// Upload all files to SharePoint in parallel
				const results = await Promise.all(
					fileArray.map((file) => uploadToSharePoint(file)),
				);

				// Filter out null results (failed uploads) and add all successful ones at once
				const successfulFiles = results.filter(
					(file): file is UploadedFile => file !== null,
				);

				if (successfulFiles.length > 0) {
					const updatedFiles = [...filesRef.current, ...successfulFiles];
					filesRef.current = updatedFiles;
					onFilesChange(updatedFiles);
					queryClient.invalidateQueries({ queryKey: ['sharePointList'] });
				}

				// Update error message if there were failures
				if (failedFilesRef.current.length > 0) {
					setUploadError(failedFilesRef.current.join('; '));
				}
			} finally {
				setIsUploading(false); // Hide loader
			}
		},
		[uploadToSharePoint, onFilesChange, disabled, clinic, onError, queryClient],
	);

	const handleDeleteFile = useCallback(
		async (fileId: string) => {
			// Find the file to delete
			const fileToDelete = files.find((file) => file.id === fileId);

			if (!fileToDelete) {
				console.error('File not found');
				return;
			}

			try {
				setIsDeleting(true);
				// Call the SharePoint delete API using the SharePoint stored filename
				const response = await deleteFileFromSharePoint({
					fileName: fileToDelete.sharePointFileName, // Use SharePoint filename, not original name
					clinic: `${clinicName.replaceAll('&', 'and')}_${fileToDelete.clinic.toUpperCase()}`,
					folderName: fileToDelete.folderName,
				});

				if (response.status === 200) {
					// Remove from local state only if API call succeeds
					const updatedFiles = files.filter((file) => file.id !== fileId);
					onFilesChange(updatedFiles);
					queryClient.invalidateQueries({ queryKey: ['sharePointList'] });
				} else {
					// Use user-friendly error message
					const cleanMessage = parseErrorMessage(
						response.message,
						'Unable to delete file. Please try again.'
					);
					throw new Error(cleanMessage);
				}
			} catch (error) {
				// Parse error to make it user-friendly
				const errorMessage = parseErrorMessage(
					error,
					'Unable to delete file. Please check your connection and try again.'
				);
				
				console.error('Error deleting file:', errorMessage);
				setUploadError(
					`Failed to delete ${fileToDelete.name}: ${errorMessage}`,
				);
				if (onError) {
					onError(errorMessage);
				}
			} finally {
				setIsDeleting(false);
			}
		},
		[files, onFilesChange, onError, parseErrorMessage, queryClient],
	);

	const formatFileSize = useCallback((bytes: number): string => {
		if (bytes === 0) return '0 Bytes';
		const k = 1024;
		const sizes = ['Bytes', 'KB', 'MB', 'GB'];
		const i = Math.floor(Math.log(bytes) / Math.log(k));
		return `${parseFloat((bytes / k ** i).toFixed(2))} ${sizes[i]}`;
	}, []);

	return {
		handleFileUpload,
		handleDeleteFile,
		formatFileSize,
		uploadError,
		isUploading,
		isDeleting,
		isDisabled: disabled || !clinic, // Return disabled state for components to use
	};
};
